import { createApi } from '@shared/api/api-factory'
import {
  clientAuthResponseSchema,
  changePasswordResponseSchema,
  confirmDobResponseSchema,
} from '@fingerprinting/types/schemas/auth.schema'
import { FINGERPRINTING_DOMAIN, FINGERPRINTING_ROUTES } from '@fingerprinting/constants/routes'

import { parseApiResponse } from './utils'

import type {
  TClientLoginFormValues,
  TChangePasswordFormValues,
  TVerifyChangePasswordFormValues,
} from '@fingerprinting/types/schemas/auth.schema'

const CLIENT_AUTH_BASE_URL = 'intake/auth' as const
const clientAuthApi = createApi({
  prefixUrl: CLIENT_AUTH_BASE_URL,
  domain: FINGERPRINTING_DOMAIN,
})

clientAuthApi.setInstanceAuthErrorHandler(() => {
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname
    if (!currentPath.startsWith(FINGERPRINTING_ROUTES.LOGIN)) {
      window.location.href = `${FINGERPRINTING_ROUTES.LOGIN}?requireAuth=true`
    }
  }
})

const clientAuthRequest = {
  login: async (data: TClientLoginFormValues) => {
    const response = await clientAuthApi.post('/login', data)
    const parsed = parseApiResponse(response.data, clientAuthResponseSchema)
    const rawResponse = response as { data?: { requiresDobConfirmation?: boolean }; requiresDobConfirmation?: boolean }
    const requiresDobConfirmation =
      parsed.data?.requiresDobConfirmation ??
      rawResponse.requiresDobConfirmation ??
      rawResponse.data?.requiresDobConfirmation ??
      false
    return { ...parsed, requiresDobConfirmation }
  },
  confirmDob: async (data: { dateOfBirth: string }) => {
    const response = await clientAuthApi.post('/confirm-dob', data)
    return parseApiResponse(response.data, confirmDobResponseSchema)
  },
  changePassword: async (data: TChangePasswordFormValues) => {
    const response = await clientAuthApi.post('/change-password', data)
    return parseApiResponse(response.data, changePasswordResponseSchema)
  },
  verifyChangePassword: async ({
    signal,
    ...data
  }: TVerifyChangePasswordFormValues & { signal?: AbortSignal }) => {
    return clientAuthApi.post('/change-password/verify', data, { signal })
  },
  forgotPassword: async (data: { email: string }) => {
    await clientAuthApi.post('/forgot-password', data)
  },
  resetPassword: async (data: { token: string; newPassword: string; confirmPassword: string }) => {
    await clientAuthApi.post('/reset-password', data)
  },
  logout: async () => {
    await clientAuthApi.post('/logout')
  },
}

export default clientAuthRequest
